import { type ComponentProps, memo } from 'react';

import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import { NotificationsHeader, type NotificationsHeaderProps } from './NotificationsHeader';
import { NotificationsList, type NotificationsListProps } from './NotificationsList';

export type NotificationsProps<Notification = unknown> = ComponentProps<'div'> &
  NotificationsListProps<Notification> &
  Pick<NotificationsHeaderProps, 'filters' | 'onFilterClick' | 'onMarkAllAsRead' | 'onSettingsClick' | 'options'> & {
    isLoading?: boolean;
  };

const _Notifications = <Notification,>({
  className,
  filters,
  getEstimateSize,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  notifications,
  onFetchNextPage,
  onFilterClick,
  onSettingsClick,
  options,
  onMarkAllAsRead,
  renderLoader,
  renderNotification,
  renderNullState,
  ...restOfProps
}: NotificationsProps<Notification>) => {
  return (
    <div className={cn('flex flex-col', className)} data-testid="NOTIFICATIONS" {...restOfProps}>
      <NotificationsHeader
        filters={filters}
        onFilterClick={onFilterClick}
        onMarkAllAsRead={onMarkAllAsRead}
        onSettingsClick={onSettingsClick}
        options={options}
      />
      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-teal-9">
          <Spinner />
        </div>
      ) : (
        <NotificationsList<Notification>
          getEstimateSize={getEstimateSize}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          notifications={notifications || []}
          onFetchNextPage={onFetchNextPage}
          renderLoader={renderLoader}
          renderNotification={renderNotification}
          renderNullState={renderNullState}
        />
      )}
    </div>
  );
};

export const Notifications = memo(_Notifications) as typeof _Notifications;
